import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { GraduationCap, Gift } from "lucide-react";

export default function CompGrantCard({ teams = [], onGranted }) {
  const [teamId, setTeamId] = useState("");
  const [months, setMonths] = useState("12");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [grants, setGrants] = useState([]);

  const loadGrants = async () => {
    try {
      const r = await api.get("/admin/comp-grants");
      setGrants(r.data?.grants || []);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { loadGrants(); }, []);

  const submit = async () => {
    if (!teamId) return toast.error("Pick a team");
    setBusy(true);
    try {
      const r = await api.post("/admin/grant-comp", {
        team_id: teamId,
        months: Number(months),
        package: "team_foundational",
        note: note || null,
      });
      toast.success(`${r.data.team_name} comped — expires ${new Date(r.data.expires_at).toLocaleDateString()}`);
      setNote("");
      loadGrants();
      onGranted?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not grant comp");
    } finally { setBusy(false); }
  };

  return (
    <Card data-testid="comp-grant-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><GraduationCap className="h-4 w-4" /> Grant comp / scholarship</CardTitle>
        <CardDescription>
          Give any team 100% off for N months. Used for HBCU scholarships, alpha-partner accounts, ambassador programs, etc.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="sm:col-span-2">
            <Label className="text-xs">Team</Label>
            <Select value={teamId} onValueChange={setTeamId}>
              <SelectTrigger data-testid="comp-team-select"><SelectValue placeholder="Pick a team…" /></SelectTrigger>
              <SelectContent>
                {teams.map((t) => (
                  <SelectItem key={t.team_id} value={t.team_id}>
                    {t.name} {t.is_comped ? "· comped" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Months</Label>
            <Select value={months} onValueChange={setMonths}>
              <SelectTrigger data-testid="comp-months-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="3">3 months</SelectItem>
                <SelectItem value="6">6 months</SelectItem>
                <SelectItem value="12">12 months</SelectItem>
                <SelectItem value="24">24 months</SelectItem>
                <SelectItem value="36">36 months</SelectItem>
                <SelectItem value="60">60 months</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label className="text-xs">Internal note</Label>
          <Input
            value={note} onChange={(e) => setNote(e.target.value)}
            placeholder='e.g. "HBCU scholarship — SUNO baseball"'
            data-testid="comp-note-input"
          />
        </div>
        <div className="flex justify-end">
          <Button onClick={submit} disabled={busy || !teamId} data-testid="comp-submit-btn">
            <Gift className="h-4 w-4 mr-1.5" /> Grant 100% off
          </Button>
        </div>

        {grants.length > 0 && (
          <div className="pt-3 border-t border-white/10">
            <div className="text-[10px] tracking-[0.25em] font-bold uppercase text-white/40 mb-2">
              Active comps ({grants.length})
            </div>
            <div className="space-y-1.5 text-sm">
              {grants.slice(0, 8).map((g, i) => (
                <div key={i} className="flex items-center justify-between rounded border border-white/5 bg-white/[0.03] p-2"
                     data-testid={`comp-row-${g.team_id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-white truncate">{g.team_name}</div>
                    <div className="text-[10px] text-white/40">
                      {g.months} months · exp {new Date(g.expires_at).toLocaleDateString()}
                      {g.note && <> · "{g.note}"</>}
                    </div>
                  </div>
                  <Badge className="text-emerald-300 border-emerald-500/40 bg-emerald-500/10">Comped</Badge>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
